import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useForm } from '@/components/react-hook-form';
import { useUpdateHouseholdIntegrationClientsMutation } from '@/modules/household/LinkHouseholdDataTrowser/SelectClientsStep/graphql/SelectClientsStep.generated';

import { PaneForm } from '../../ManageIntegrationDataModal.types';
import {
  hasClientChanges,
  SelectClientsFormShape,
  toUpdateHouseholdInput,
} from './selectClients.utils';

export interface SelectClientsForm extends PaneForm {
  formMethods: UseFormReturn<SelectClientsFormShape>;
  seed: (integrationClientIds: string[]) => void;
}

export function useSelectClientsForm(
  householdId: string,
  resolveClientIds?: (ids: string[]) => Promise<string[]>,
  onSaved?: () => void
): SelectClientsForm {
  const { createErrorFeedback } = useFeedback();

  const formMethods = useForm<SelectClientsFormShape>({
    defaultValues: { integrationClientIds: [] },
  });
  const {
    reset,
    handleSubmit,
    formState: { isDirty, defaultValues },
  } = formMethods;

  const seed = useCallback(
    (integrationClientIds: string[]) => {
      if (isDirty) return;
      reset({ integrationClientIds });
    },
    [isDirty, reset]
  );

  // No `onError`: Apollo only rejects the promise when the caller hasn't
  // handled the error, and the commit needs that rejection to stop.
  const [updateHousehold] = useUpdateHouseholdIntegrationClientsMutation();

  const save = handleSubmit(async (values) => {
    try {
      // Live provider selections must resolve to cached client IDs before the diff.
      const resolvedValues = resolveClientIds
        ? {
            integrationClientIds: await resolveClientIds(
              values.integrationClientIds
            ),
          }
        : values;
      const input = toUpdateHouseholdInput(
        resolvedValues,
        defaultValues?.integrationClientIds
      );
      if (hasClientChanges(input)) {
        await updateHousehold({ variables: { householdId, input } });
        onSaved?.();
      }
      reset(resolvedValues);
    } catch (error) {
      createErrorFeedback(
        'Failed to update integration clients. Please try again.'
      )();
      throw error;
    }
  });

  return { formMethods, save, seed };
}
